import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzCascaderModule } from 'ng-zorro-antd/cascader';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { TranslateService } from '@ngx-translate/core';
import { translateTAValue } from 'src/app/system-utils/pipes/translateTA';
import { AtendimentoDeAbertura, ChamadosService } from '../services/chamados.service';
import { AnexosService } from '../shared/anexos.service';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { abrirAbaDeChamados } from '../shared/abrir-aba';
import { caminhoDaCategoria, montarArvore, montarCascata, OpcaoCascata } from '../config/categorias/arvore';
import { TabService } from 'src/app/services/tab.service';
import { TicketAttachment, TicketCategory, TicketConfig, TicketQueue } from '../models/chamados.models';

/**
 * Abertura de chamado — conteúdo do modal.
 *
 * Abrir chamado é uma AÇÃO, não um lugar: não tem item de menu nem rota
 * própria. Ele é chamado de onde a pessoa já está — do balão flutuante e da
 * tela de acompanhamento — por `abrirModalDeChamado`.
 *
 * O formulário não grava no Mongo: manda para o `TicketServices/open`, o mesmo
 * caminho por onde o WhatsApp e o Telegram abrem chamado. É o que garante que
 * numeração, fila, prioridade e prazos de SLA saiam iguais nos três canais.
 *
 * STANDALONE de propósito: ele é aberto tanto de dentro do módulo lazy quanto
 * do shell autenticado, e um componente só pode ser declarado em um NgModule.
 * A consequência é que ele NÃO pode usar o pipe `translateTA` (que é
 * `standalone: false`) — daí o método `t()`, sobre a mesma função
 * `translateTAValue` que o pipe usa. Duas regras de tradução divergiriam; uma
 * função compartilhada, não.
 */
@Component({
    selector: 'chamado-novo',
    templateUrl: './chamado-novo.component.html',
    styleUrl: './chamado-novo.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NzButtonModule,
        NzInputModule,
        NzSelectModule,
        NzCascaderModule,
        NzIconModule,
        NzAlertModule,
        NzTagModule,
        NzToolTipModule,
        NzResultModule,
        NzSpinModule,
        NzUploadModule,
    ],
})
export class ChamadoNovoComponent implements OnInit {

    carregando = false;
    enviando = false;

    /** TODAS as categorias ativas: a árvore precisa dos grupos também. */
    categorias: TicketCategory[] = [];

    /**
     * As opções em cascata, já podadas.
     *
     * Só as folhas que a pessoa pode escolher, e só os grupos que levam a
     * alguma dessas folhas.
     */
    opcoes: OpcaoCascata[] = [];

    /** Caminho escolhido: `['financeiro', 'boletos', 'segundaVia']`. */
    caminhoEscolhido: string[] = null;

    /**
     * Recria o cascader.
     *
     * Sem `ngModel` nao ha como limpar o controle de fora; destruir e recriar
     * e o jeito honesto — e acontece uma vez, no "abrir outro".
     */
    cascaderVisivel = true;

    config: TicketConfig = null;

    // ─────────────── de quem é o chamado ───────────────

    /**
     * Os ATENDIMENTOS onde se pode abrir chamado — um por configuração.
     *
     * NÃO são os contratos do cadastro de quem está logado. Quem abre quase
     * nunca pertence ao contrato da central: o cliente é do contrato dele, a
     * central é de outro, e é a configuração DELA que traz fila, categoria,
     * SLA e prazo. Olhar o contrato do usuário procurava a configuração no
     * lugar errado e não achava categoria nenhuma.
     *
     * Com mais de uma, a pessoa escolhe antes de qualquer outra coisa.
     */
    contratosConfiguracoes: { codecontract: number, nome: string }[] = [];
    contratosConfiguracaoEscolhido: number = null;

    contratosDoCliente: { codecontract: number, nome: string }[] = [];
    contratoClienteEscolhido: number = null;

    /**
     * Tudo o que a pessoa pode escolher, já pronto do servidor.
     *
     * Vem numa chamada só (`TicketServices/openingOptions`) porque trocar de
     * atendimento não pode custar três consultas — e porque quem decide o que
     * ela pode ver é o servidor, não esta tela.
     */
    private atendimentos: AtendimentoDeAbertura[] = [];

    empresas: { codecompany: number, nome: string }[] = [];
    empresaEscolhida: number = null;

    /** Filas, para saber se a categoria escolhida pede a empresa. */
    filas: TicketQueue[] = [];


    /** Anexos ja enviados, prontos para ir junto. */
    anexos: TicketAttachment[] = [];

    /** Um de cada vez: a barra de progresso e por arquivo. */
    enviandoAnexo = false;

    /**
     * Sobe o arquivo escolhido e guarda o anexo.
     *
     * O upload acontece AGORA, e nao no envio: assim a pessoa ve o arquivo
     * aceito (ou o erro) enquanto ainda esta escrevendo, e nao depois de
     * clicar em enviar. Falha aqui nao perde o texto.
     *
     * Devolve `false` para o nz-upload nao tentar subir sozinho — quem sobe e
     * o servico, para o endereco do arquivo voltar para ca.
     */
    aoEscolherAnexo = (arquivo: NzUploadFile): boolean => {

        const bruto = (arquivo as unknown as File);

        if (!this.anexosService.cabe(bruto)) {
            this.message.error(this.t('tkt.anexo.grande'));
            return false;
        }

        this.enviandoAnexo = true;

        this.anexosService.enviar(bruto, this.anexosService.pastaDaAbertura()).subscribe({
            next: anexo => {
                this.anexos.push(anexo);
                this.enviandoAnexo = false;
            },
            error: () => {
                this.message.error(this.t('tkt.anexo.erro'));
                this.enviandoAnexo = false;
            },
        });

        return false;
    };

    removerAnexo(indice: number): void {
        // So tira da lista: o arquivo continua no bucket. Apagar de la exigiria
        // uma operacao de exclusao que nao existe, e um anexo orfao e mais
        // barato que um anexo que sumiu de um chamado ja aberto.
        this.anexos.splice(indice, 1);
    }

    assunto = '';
    descricao = '';

    /**
     * O ultimo assunto que a TELA preencheu sozinha.
     *
     * Serve para saber se pode reescrever: enquanto o campo tiver exatamente
     * o que a tela pos, trocar de categoria troca o assunto junto; assim que
     * a pessoa digita por cima, o que ela escreveu manda.
     */
    private assuntoAutomatico = '';

    /**
     * O chamado que este continua, quando a pessoa clicou em "abrir novo" a
     * partir de um encerrado.
     *
     * Chega pelo `nzData` do modal. Sem ele, o novo chamado nasce solto e o
     * atendente recebe "o mesmo problema de novo" sem nada que ligue os dois.
     */
    readonly origem: { ticketId: string, numero: string } = inject(NZ_MODAL_DATA, { optional: true });

    /** Chamado recém-aberto. Preenchido = confirmação no lugar do formulário. */
    criado: { ticketId: string, numero: string } = null;

    constructor(
        private service: ChamadosService,
        private message: NzMessageService,
        private router: Router,
        private tabService: TabService,
        private anexosService: AnexosService,
        private modalRef: NzModalRef,
        private translate: TranslateService
    ) { }

    /** Tradução para o template. Ver a nota de STANDALONE na classe. */
    t(chave: string): string {
        return translateTAValue(this.translate, chave);
    }

    get perguntaConfiguracoes(): boolean {
        return this.contratosConfiguracoes.length > 1;
    }

    get perguntaCliente(): boolean {
        return this.contratosDoCliente.length > 1;
    }

    ngOnInit(): void {

        this.carregando = true;

        this.service.opcoesDeAbertura().subscribe({
            next: atendimentos => {
                this.atendimentos = atendimentos ?? [];
                this.contratosConfiguracoes = this.atendimentos.map(item => ({
                    codecontract: item.codecontract,
                    nome: item.nome,
                }));
                this.contratosDoCliente = this.service.contratosDoUsuario();
                this.escolherAtendimento();
                this.escolherContratoCliente();
            },
            error: () => {
                this.atendimentos = [];
                this.contratosConfiguracoes = [];
                this.message.error(this.t('tkt.novo.loadError'));
                this.escolherAtendimento();
                this.escolherContratoCliente();
            },
        });
    }

    /**
     * Decide em qual atendimento abrir.
     *
     * Um só: nem pergunta. O seletor aparece quando há escolha de verdade a
     * fazer.
     */
    private escolherAtendimento(): void {

        this.contratosConfiguracaoEscolhido = this.atendimentos.length === 1
            ? this.atendimentos[0].codecontract
            : null;

        this.atualizarEmpresas();
        this.carregar();
    }


    private escolherContratoCliente(): void {

        this.contratoClienteEscolhido = this.contratosDoCliente.length === 1
            ? this.contratosDoCliente[0].codecontract
            : null;

        this.atualizarEmpresas();
        
    }

    /** Precisa perguntar em qual atendimento abrir? */
    get perguntaContrato(): boolean {
        return this.contratosConfiguracoes.length > 1;
    }

    /**
     * As empresas que a pessoa pode indicar.
     *
     * Vêm do cadastro DELA, e não do atendimento escolhido: a empresa diz de
     * qual unidade do cliente é o problema, e o cliente é ela. Filtrar pelo
     * contrato da central devolveria lista vazia — ela não tem empresa lá.
     */
    private atualizarEmpresas(): void {

        this.empresas = this.service.empresasDoContrato(null);

        // Uma empresa só no contrato: ela já vem escolhida, e o campo some.
        if (this.empresas.length === 1) {
            this.empresaEscolhida = this.empresas[0].codecompany;
        }
    }

    onContratoClienteChange(): void {
        this.atualizarEmpresas();
    }


    /** Trocou o atendimento: configuração, categorias e filas mudam junto. */
    onContratoChange(): void {

        this.contratoClienteEscolhido = null;
        this.caminhoEscolhido = null;
        this.empresaEscolhida = null;
        this.cascaderVisivel = false;
        setTimeout(() => this.cascaderVisivel = true);

        this.atualizarEmpresas();
        this.carregar();
    }

    /**
     * A fila da categoria escolhida decide se a empresa é perguntada.
     *
     * A pergunta nasce da FILA, e não da tela: é quem atende que sabe se
     * precisa saber de qual unidade do cliente veio o problema.
     */
    get empresaNaAbertura(): string {

        const categoria = this.categoriaSelecionada;

        if (categoria?.filaId == null) {
            return 'NAO';
        }

        const fila = this.filas.find(item => item._id === categoria.filaId);

        return fila?.empresaNaAbertura ?? 'NAO';
    }

    get perguntaEmpresa(): boolean {
        return this.empresaNaAbertura !== 'NAO' && this.empresas.length > 1;
    }

    get empresaObrigatoria(): boolean {
        return this.empresaNaAbertura === 'OBRIGATORIA' && this.empresas.length > 1;
    }

    /**
     * Aplica o atendimento escolhido.
     *
     * Sem ida ao servidor: o `openingOptions` já trouxe tudo, e o que a tela
     * faz aqui é escolher qual dos atendimentos vale agora.
     */
    private carregar(): void {

        const atendimento = this.atendimentos
            .find(item => item.codecontract === this.contratosConfiguracaoEscolhido);

        if (atendimento == null) {
            this.categorias = [];
            this.filas = [];
            this.config = null;
            this.opcoes = [];
            this.carregando = false;
            return;
        }

        this.categorias = atendimento.categorias ?? [];
        this.filas = atendimento.filas ?? [];
        this.config = atendimento.config as TicketConfig;

        // O servidor já tirou o que esta pessoa não pode escolher; aqui só
        // sobra montar a árvore e podar grupo que ficou sem folha.
        this.opcoes = montarCascata(
            montarArvore(this.categorias), categoria => categoria?.active !== false);

        this.carregando = false;
    }


    /**
     * O caminho que a pessoa percorreu até a folha.
     *
     * O evento entrega os NÓS; guardamos só os ids, que é o que interessa.
     * Fechar sem escolher emite lista vazia — e aí o campo volta a ficar vazio.
     */
    onCategoriaEscolhida(opcoes: { value?: string | number }[]): void {

        this.caminhoEscolhido = (opcoes ?? [])
            .map(opcao => String(opcao.value))
            .filter(valor => valor !== 'undefined' && valor !== 'null');

        if (this.caminhoEscolhido.length === 0) {
            this.caminhoEscolhido = null;
        }

        this.preencherAssuntoPelaCategoria();
    }

    /**
     * O assunto nasce do CAMINHO da categoria: `Financeiro › Boletos › 2ª via`.
     *
     * So o nome da folha ("Segunda via") nao diz do que se trata na lista da
     * mesa — o sentido esta no caminho inteiro. E deixar o campo em branco
     * empurra para o atendente a tarefa de adivinhar pelo texto.
     *
     * Continua editavel: e sugestao, nao imposicao. Por isso so escreve
     * quando o campo esta vazio ou quando ainda tem o que a propria tela pos.
     */
    private preencherAssuntoPelaCategoria(): void {

        const caminho = this.caminhoLegivel;

        if (caminho.length === 0) {
            return;
        }

        if (this.assunto.trim().length > 0 && this.assunto !== this.assuntoAutomatico) {
            return;
        }

        this.assunto = caminho;
        this.assuntoAutomatico = caminho;
    }

    /** Id escolhido: o ÚLTIMO do caminho, que é sempre a folha. */
    get categoriaId(): string {

        if (this.caminhoEscolhido == null || this.caminhoEscolhido.length === 0) {
            return null;
        }

        return this.caminhoEscolhido[this.caminhoEscolhido.length - 1];
    }

    /** Caminho legível, para a confirmação mostrar onde o chamado caiu. */
    get caminhoLegivel(): string {

        const categoria = this.categoriaSelecionada;

        return categoria != null ? caminhoDaCategoria(categoria, this.categorias) : '';
    }

    get categoriaSelecionada(): TicketCategory {

        if (this.categoriaId == null) {
            return null;
        }

        return this.categorias.find(categoria => categoria._id === this.categoriaId) ?? null;
    }

    /**
     * Prioridade que o chamado VAI receber.
     *
     * Mostrada, e não escolhida: quem abre não define a própria urgência. Se
     * definisse, todo chamado entraria como CRÍTICA e a fila perderia a ordem.
     * A regra é a mesma do backend — categoria primeiro, configuração depois.
     */
    get prioridadePrevista(): string {

        const daCategoria = this.categoriaSelecionada?.prioridade;

        if (daCategoria != null) {
            return daCategoria;
        }

        return this.config?.prioridadePadrao ?? 'NORMAL';
    }

    prioridadeLabel(prioridade: string): string {
        if (prioridade == null) {
            return '';
        }
        return this.t('tkt.prioridade.' + prioridade);
    }

    /** Prazo de primeira resposta da categoria, em texto curto. */
    get slaPrevisto(): string {

        const minutos = this.categoriaSelecionada?.sla?.primeiraRespostaMin;

        if (minutos == null || minutos <= 0) {
            return null;
        }

        if (minutos < 60) {
            return `${minutos}min`;
        }

        const horas = Math.floor(minutos / 60);
        const resto = minutos % 60;

        return resto === 0 ? `${horas}h` : `${horas}h ${resto}min`;
    }

    /** Sem folha publicada no app, o backend usa a categoria padrão. */
    get semCategorias(): boolean {
        return !this.carregando && this.opcoes.length === 0;
    }

    get podeEnviar(): boolean {

        if (this.enviando) {
            return false;
        }

        if (this.assunto.trim().length === 0 || this.descricao.trim().length === 0) {
            return false;
        }

        if (this.perguntaConfiguracoes && this.contratosConfiguracaoEscolhido == null) {
            return false;
        }

        if (this.empresaObrigatoria && this.empresaEscolhida == null) {
            return false;
        }

        // Categoria só é exigida quando existe alguma para escolher.
        return this.semCategorias || this.categoriaId != null;
    }

    abrir(): void {

        if (!this.podeEnviar) {
            return;
        }

        this.enviando = true;

        this.service.abrirChamado({
            codecontractConfiguracao: this.contratosConfiguracaoEscolhido,
            codecontract: this.contratoClienteEscolhido,
            codecompany: this.empresaEscolhida,
            categoriaId: this.categoriaId,
            assunto: this.assunto.trim(),
            descricao: this.descricao.trim(),
            anexos: this.anexos,
            ticketPaiId: this.origem?.ticketId,
            ticketPaiNumero: this.origem?.numero,
        }).subscribe({
            next: resposta => {
                this.enviando = false;
                this.criado = resposta;
            },
            error: erro => {
                this.enviando = false;
                this.message.error(this.mensagemDoErro(erro) ?? this.t('tkt.novo.abrirError'));
            },
        });
    }

    private mensagemDoErro(erro: unknown): string {

        const resposta = erro as { error?: { message?: string } };

        if (resposta?.error?.message != null && resposta.error.message.trim().length > 0) {
            return resposta.error.message;
        }

        return null;
    }

    /** Limpa e volta ao formulário, para abrir outro sem fechar o modal. */
    abrirOutro(): void {

        this.criado = null;
        this.caminhoEscolhido = null;

        // Recria o cascader para ele voltar visualmente ao vazio.
        this.cascaderVisivel = false;
        setTimeout(() => this.cascaderVisivel = true);

        this.assunto = '';
        this.assuntoAutomatico = '';
        this.descricao = '';
        this.anexos = [];
    }

    /**
     * Fecha devolvendo o que foi criado.
     *
     * Quem abriu o modal usa esse retorno para recarregar a lista — sem ele, o
     * chamado recém-aberto só apareceria na próxima atualização automática.
     */
    fechar(): void {
        this.modalRef.close(this.criado);
    }

    verMeusChamados(): void {
        this.modalRef.close(this.criado);
        abrirAbaDeChamados(this.tabService, this.router, 'chamados-meus', 'chamados-meus', 'inbox');
    }
}
